import { TableRowActionsMenu } from "@/block-system/blocks/Table/Block/TableActions";
import { CellRenderers } from "@/block-system/blocks/Table/Block/TableCell";
import {
  getTableHeaderElementId,
  TableHeaderCell,
} from "@/block-system/blocks/Table/Block/TableHeaderCell";
import {
  SUPPORTED_TABLE_BLOCK_SORT_BY_FIELD_TYPES,
  TABLE_RECORDS_PAGINATION_PAGE_SIZE,
  TABLE_QUERY_STALE_TIME,
  TABLE_ROW_ACTIONS_COLUMN_ID,
} from "@/block-system/blocks/Table/constants";
import {
  filterUnsupportedTableFields,
  type SupportedTableBlockFields,
} from "@/block-system/blocks/Table/lib/filterUnsupportedTableFields";
import type { FieldFilter } from "@/block-system/types/base";
import { usePageContext } from "@/lib/context/page-context";
import { zrpc } from "@/lib/zrpc";
import type { BlockTableQueryRecordsInput } from "@/server/schemas/blockTable";
import type { RecordOrder } from "@/server/schemas/table-records";
import type {
  APIRecord,
  APIUpdateRecordsResponse,
} from "@/server/services/tables/types/records";
import type { APIFieldLatest } from "@/server/services/tables/types/tables-latest";
import { trpc } from "@/utils/trpc";
import {
  createColumnHelper,
  type OnChangeFn,
  type SortingState,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { sortBy } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { type TableBlock as TableBlockType } from "../types";
import { useInvalidateBlocksUsingTable } from "@/block-system/blocks/Table/lib/invalidateTableRecordQueries";

export type FetchPageFn = ({
  direction,
}: {
  direction: "next" | "previous" | "first" | "last";
}) => void;

type TableRecordsPaginationParameters = {
  cursorQuery: string | undefined;
  pageSize: number;
  sortingState: SortingState;
  query: string;
  links?: {
    next?: string | undefined;
    prev?: string | undefined;
  };
};

export function useTableRecords({
  tableId,
  blockId,
  blockFilters = [],
  initialOrders = [],
  initialQuery = "",
  initialPageSize,
  onPageChange = () => {},
}: {
  tableId: string | undefined;
  blockId: string | undefined;
  initialPageSize: TABLE_RECORDS_PAGINATION_PAGE_SIZE;
  blockFilters?: Array<FieldFilter>;
  initialOrders?: Array<RecordOrder>;
  initialQuery?: string;
  onPageChange?: VoidFunction;
}) {
  const latestOnPageChange = useRef<VoidFunction>(onPageChange);
  latestOnPageChange.current = onPageChange;

  const [paginationParameters, setPaginationParameters] =
    useState<TableRecordsPaginationParameters>(() => {
      return {
        cursorQuery: undefined,
        links: {
          next: undefined,
          prev: undefined,
        },
        pageSize: initialPageSize,
        sortingState: sortOrderToSortingState(initialOrders),
        query: initialQuery,
      };
    });

  const setCursorQuery = useCallback(
    ({
      cursorQuery,
      links,
    }: {
      cursorQuery: string | undefined;
      links?: {
        next?: string | undefined;
        prev?: string | undefined;
      };
    }) => {
      setPaginationParameters((prev) => {
        return {
          query: "",
          links,
          cursorQuery,
          pageSize: prev.pageSize,
          sortingState: prev.sortingState,
        };
      });
    },
    []
  );

  const setSortingState: OnChangeFn<SortingState> = useCallback(
    (sortingStateUpdater) => {
      setPaginationParameters((prev) => {
        const newSortingState =
          typeof sortingStateUpdater === "function"
            ? sortingStateUpdater(prev.sortingState)
            : sortingStateUpdater;

        return {
          query: "",
          /**
           * We have to ensure the cursor is reset when switching the sort state.
           * 1. Sort by field A.
           * 2. Click on "next page".
           * 3. Sort by field B.
           * Now the cursor our query has is "tied" with the "sort by field A" state.
           * If we send that cursor to the Tables API, the API will throw an error, because the cursor is invalid for the new sort state.
           */
          cursorQuery: undefined,
          pageSize: prev.pageSize,
          sortingState: newSortingState,
        };
      });
    },
    []
  );

  const setQuery = useCallback((query: string) => {
    setPaginationParameters((prev) => {
      return {
        query,
        /**
         * We do not support pagination when searching
         */
        cursorQuery: undefined,
        sortingState: prev.sortingState,
        pageSize: prev.pageSize,
      };
    });
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setPaginationParameters((prev) => {
      return {
        query: "",
        /**
         * We should reset the cursor when changing the page size.
         * 1. Have 11 records in the table.
         * 2. Set page size to 10.
         * 3. Go to the last page.
         * 4. The last page should contain only 1 record.
         * 5. Change the page size to 20.
         * 6. The page should contain all the records.
         * 7. Change the page size back to 10.
         * 8. In theory, now we should be displaying only 1 record, but how do we know if we were on the last page before changing the page size?
         * To avoid this complexity, we will just reset the cursor.
         */
        cursorQuery: undefined,
        pageSize,
        sortingState: prev.sortingState,
      };
    });
  }, []);

  /**
   * As soon as we have more than 1 filter, we can't paginate the results.
   * Two filters means that we have "OR" relationship between them, and that is not supported by the Tables API.
   */
  const hasMultipleBlockFilters = blockFilters.length > 1;
  const hasQuery = paginationParameters.query.length > 0;

  const canPaginateRecords = !hasMultipleBlockFilters && !hasQuery;
  const canSortRecords = !hasMultipleBlockFilters && !hasQuery;

  const {
    count: recordsCount,
    isSupported: canCountRecords,
    isLoading: isLoadingRecordsCount,
  } = useCountTableRecords({
    blockId,
    tableId,
    orders: sortingStateToSortOrder(paginationParameters.sortingState),
    /**
     * We only use this data in the context of pagination.
     * There is no point to fetch it when the pagination is disabled.
     */
    enabled: canPaginateRecords === true,
  });

  const { queryRecordsQueryInput, getCurrentQueryInput } =
    useTableRecordsQueryInput({
      paginationParameters,
      blockId,
      tableId,
      recordsCount,
      hasMultipleBlockFilters,
    });

  const {
    data = {
      results: [],
      links: {},
      meta: { cursor: undefined, next_cursor: undefined, limit: undefined },
    },
    /**
     * TODO: Handle errors
     */
    isLoading: isLoadingRecords,
    isPlaceholderData: isFetchingRecords,
  } = zrpc.interfaces.useQuery(
    "get",
    "/api/interfaces/v0/blocks/table/{block_id}/tables/{table_id}/records",
    {
      params: {
        path: {
          block_id: blockId as string,
          table_id: tableId as string,
        },
        query: {
          orders: JSON.stringify(queryRecordsQueryInput.filter.orders),
          limit: queryRecordsQueryInput.filter.limit,
          cursor: queryRecordsQueryInput.cursor,
          query: queryRecordsQueryInput.query,
        },
      },
    },
    {
      placeholderData: (previousData) => {
        return previousData;
      },
      enabled: tableId != null && blockId != null,
      staleTime: TABLE_QUERY_STALE_TIME,
    }
  );

  const { results, meta, links } = data;

  const canFetchNextPage = links.next != undefined && !isFetchingRecords;
  const canFetchPreviousPage = links.prev != undefined && !isFetchingRecords;

  /**
   * To properly jump to the last page, we have to count the records in the table.
   * The endpoints we use to count records does not support Table views.
   * If the Table block is using a view, we should skip rendering the "jump to first page" and "jump to last page" buttons.
   *
   * In theory, we _could_ render the "jump to the first page" button, as it does not require records count to function,
   * but we deemed that a bit confusing for the user.
   */
  const supportsPageJumping = canCountRecords === true;

  const handlePageFetch: FetchPageFn = useCallback(
    ({ direction }) => {
      if (!canPaginateRecords) {
        return;
      }

      switch (direction) {
        case "next": {
          if (!canFetchNextPage) {
            return;
          }

          setCursorQuery({ cursorQuery: meta.next_cursor, links });

          break;
        }

        case "previous": {
          if (!canFetchPreviousPage) {
            return;
          }

          setCursorQuery({ cursorQuery: meta.cursor, links });
          break;
        }

        case "first": {
          if (!canFetchPreviousPage) {
            return;
          }

          if (!supportsPageJumping) {
            return;
          }

          setCursorQuery({ cursorQuery: "", links });
          break;
        }

        case "last": {
          if (!canFetchNextPage) {
            return;
          }

          if (!supportsPageJumping) {
            return;
          }

          setCursorQuery({
            cursorQuery: Buffer.from(
              JSON.stringify({ query_type: "before" })
            ).toString("base64"),
          });
          break;
        }

        default: {
          const exhaustiveCheck: never = direction;
          throw new Error(`Unhandled direction case: ${exhaustiveCheck}`);
        }
      }
    },
    [
      canPaginateRecords,
      canFetchNextPage,
      setCursorQuery,
      meta.next_cursor,
      meta.cursor,
      links,
      canFetchPreviousPage,
      supportsPageJumping,
    ]
  );

  const fetchPage: FetchPageFn = useCallback(
    (...props) => {
      handlePageFetch(...props);
      latestOnPageChange.current();
    },
    [handlePageFetch]
  );

  return {
    results,
    sortingState: paginationParameters.sortingState,
    isLoadingRecords: isLoadingRecords || isLoadingRecordsCount,
    isFetchingRecords,
    setSortingState,
    query: paginationParameters.query,
    setQuery,
    fetchPage,
    supportsPageJumping,
    canFetchNextPage,
    canFetchPreviousPage,
    canPaginateRecords,
    canSortRecords,
    pageSize: paginationParameters.pageSize,
    setPageSize,
    getCurrentQueryInput,
  };
}

function useTableRecordsQueryInput({
  paginationParameters,
  blockId,
  tableId,
  recordsCount,
  hasMultipleBlockFilters,
}: {
  paginationParameters: TableRecordsPaginationParameters;
  blockId: string | undefined;
  tableId: string | undefined;
  recordsCount: number | null | undefined;
  hasMultipleBlockFilters: boolean;
}) {
  const limit = calculateQueryRecordsLimit({
    pageSize: paginationParameters.pageSize,
    cursorQuery: paginationParameters.cursorQuery,
    recordsCount: recordsCount,
    hasMultipleBlockFilters,
  });

  const queryRecordsQueryInput: BlockTableQueryRecordsInput = {
    filter: {
      limit,
      orders: sortingStateToSortOrder(paginationParameters.sortingState),
    },
    cursor: paginationParameters.cursorQuery,
    query: paginationParameters.query,
    tableId: tableId as string,
    blockId: blockId as string,
  };

  const latestQueryInput = useRef<BlockTableQueryRecordsInput>(
    queryRecordsQueryInput
  );
  latestQueryInput.current = queryRecordsQueryInput;

  const getCurrentQueryInput = useCallback(() => {
    return latestQueryInput.current;
  }, []);

  return { queryRecordsQueryInput, getCurrentQueryInput };
}

type TableRecordsUtilsContextType = {
  getCurrentQueryInput: () => BlockTableQueryRecordsInput;
  removeRecordFromCache: ({ recordId }: { recordId: string }) => void;
  incrementRecordCountInCache: () => void;
  updateRecordInCache: ({
    recordId,
    newRecord,
  }: {
    recordId: string;
    newRecord: Partial<APIUpdateRecordsResponse["data"][number]["new"]>;
  }) => void;
};

const TableRecordsUtilsContext =
  createContext<TableRecordsUtilsContextType | null>(null);

export function TableRecordsUtilsProvider({
  children,
  getCurrentQueryInput,
  fetchPage,
}: {
  children: React.ReactNode;
  getCurrentQueryInput: TableRecordsUtilsContextType["getCurrentQueryInput"];
  fetchPage: FetchPageFn;
}) {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const invalidateBlocksUsingTable = useInvalidateBlocksUsingTable();

  const updateRecordCountInCache = useCallback(
    ({ operand }: { operand: 1 | -1 }) => {
      const currentQueryInput = getCurrentQueryInput();

      utils.blockTable.countRecords.setData(
        {
          blockId: currentQueryInput.blockId,
          tableId: currentQueryInput.tableId,
          filter: { orders: currentQueryInput.filter.orders },
        },
        (countRecordsDataInCache) => {
          if (!countRecordsDataInCache) {
            return countRecordsDataInCache;
          }

          if (countRecordsDataInCache.supported === false) {
            return countRecordsDataInCache;
          }

          return {
            ...countRecordsDataInCache,
            count: countRecordsDataInCache.count + operand,
          };
        }
      );
    },
    [getCurrentQueryInput, utils.blockTable.countRecords]
  );

  const decrementRecordCountInCache = useCallback(() => {
    updateRecordCountInCache({ operand: -1 });
  }, [updateRecordCountInCache]);

  const incrementRecordCountInCache = useCallback(() => {
    updateRecordCountInCache({ operand: 1 });
  }, [updateRecordCountInCache]);

  const removeRecordFromCache: TableRecordsUtilsContextType["removeRecordFromCache"] =
    useCallback(
      ({ recordId }: { recordId: string }) => {
        decrementRecordCountInCache();

        const currentQueryInput = getCurrentQueryInput();

        const queryOptions = zrpc.interfaces.queryOptions(
          "get",
          "/api/interfaces/v0/blocks/table/{block_id}/tables/{table_id}/records",
          {
            params: {
              path: {
                block_id: currentQueryInput.blockId,
                table_id: currentQueryInput.tableId,
              },
              query: {
                orders: JSON.stringify(currentQueryInput.filter.orders),
                limit: currentQueryInput.filter.limit,
                cursor: currentQueryInput.cursor,
                query: currentQueryInput.query,
              },
            },
          }
        );

        const dataInCacheBeforeUpdate = queryClient.getQueryData<
          Awaited<ReturnType<typeof queryOptions.queryFn>>
        >(queryOptions.queryKey);

        if (hasRemovedLastRecordOnLastPage(dataInCacheBeforeUpdate)) {
          /**
           * See https://zapierorg.atlassian.net/browse/TBLS-4368
           */
          return fetchPage({ direction: "previous" });
        }

        queryClient.setQueryData<
          Awaited<ReturnType<typeof queryOptions.queryFn>>
        >(queryOptions.queryKey, (dataInCache) => {
          if (!dataInCache) {
            return dataInCache;
          }

          const { results, meta, links } = dataInCache;
          return {
            results: results.filter((record) => {
              return record.id !== recordId;
            }),
            meta,
            links,
          };
        });

        /**
         * 1. We have to invalidate the query because the record we have deleted might be the last record for a given page.
         * If that is the case, the pagination cursors need to be invalidated as well.
         *
         * 2. If a user is deleting a record that they searched for, we have to ensure the record also does not appear when they clear the search.
         * Note that the record might be in cache multiple times, depending on how many cache entries with "query" are present.
         * For this reason, we are using `exact: false`, as we do not have the ability to calculate the cache key for all those entries.
         */
        // Create query options without the query parameter
        void invalidateBlocksUsingTable(currentQueryInput.tableId);
      },
      [
        decrementRecordCountInCache,
        getCurrentQueryInput,
        queryClient,
        invalidateBlocksUsingTable,
        fetchPage,
      ]
    );

  const updateRecordInCache: TableRecordsUtilsContextType["updateRecordInCache"] =
    useCallback(
      ({ recordId, newRecord }) => {
        const currentQueryInput = getCurrentQueryInput();

        const queryOptions = zrpc.interfaces.queryOptions(
          "get",
          "/api/interfaces/v0/blocks/table/{block_id}/tables/{table_id}/records",
          {
            params: {
              path: {
                block_id: currentQueryInput.blockId,
                table_id: currentQueryInput.tableId,
              },
              query: {
                orders: JSON.stringify(currentQueryInput.filter.orders),
                limit: currentQueryInput.filter.limit,
                cursor: currentQueryInput.cursor,
                query: currentQueryInput.query,
              },
            },
          }
        );

        queryClient.setQueryData<
          Awaited<ReturnType<typeof queryOptions.queryFn>>
        >(queryOptions.queryKey, (dataInCache) => {
          if (!dataInCache) {
            return dataInCache;
          }

          const { results, meta, links } = dataInCache;
          return {
            results: results.map((record) => {
              if (record.id === recordId) {
                return { ...record, ...newRecord };
              }

              return record;
            }),
            meta,
            links,
          };
        });
      },
      [getCurrentQueryInput, queryClient]
    );

  const value = useMemo(() => {
    return {
      getCurrentQueryInput,
      removeRecordFromCache,
      updateRecordInCache,
      incrementRecordCountInCache,
    };
  }, [
    getCurrentQueryInput,
    incrementRecordCountInCache,
    removeRecordFromCache,
    updateRecordInCache,
  ]);

  return (
    <TableRecordsUtilsContext.Provider value={value}>
      {children}
    </TableRecordsUtilsContext.Provider>
  );
}

export function useTableRecordsUtils() {
  const context = useContext(TableRecordsUtilsContext);

  if (!context) {
    throw new Error(
      `${useTableRecordsUtils.name} must be used within a ${TableRecordsUtilsProvider.name}`
    );
  }

  return context;
}

function useCountTableRecords({
  blockId,
  tableId,
  orders,
  enabled = true,
}: {
  blockId: string | undefined;
  tableId: string | undefined;
  orders: Array<RecordOrder>;
  enabled?: boolean;
}) {
  const isQueryEnabled = tableId != null && blockId != null && enabled === true;

  const { data, isLoading, isError, isPlaceholderData } =
    trpc.blockTable.countRecords.useQuery(
      {
        tableId: tableId as string,
        blockId: blockId as string,
        filter: { orders },
      },
      {
        /**
         * The number is arbitrary.
         * We have added it in attempt to reduce calls to the Tables API.
         */
        staleTime: TABLE_QUERY_STALE_TIME,
        enabled: isQueryEnabled,
        placeholderData: (previousData) => {
          return previousData;
        },
      }
    );

  return {
    count: data?.count,
    isSupported: data?.supported === true,
    isLoading,
    isError,
    isPlaceholderData,
  };
}

export function useTableFields({
  blockId,
  tableId,
  hiddenFields = [],
  columnOrder = [],
}: {
  blockId: string | undefined;
  tableId: string | undefined;
  hiddenFields: TableBlockType["config"]["hiddenFields"];
  columnOrder: TableBlockType["config"]["columnOrder"];
}) {
  const { data: fieldsResponse, isLoading: isLoadingFields } =
    zrpc.interfaces.useQuery(
      "get",
      "/api/interfaces/v0/blocks/table/{block_id}/tables/{table_id}/fields",
      {
        params: {
          path: {
            block_id: blockId as string,
            table_id: tableId as string,
          },
        },
      },
      {
        staleTime: TABLE_QUERY_STALE_TIME,
        enabled: blockId !== undefined && tableId !== undefined,
        select: ({ results }) => {
          const [supportedFields = []] = filterUnsupportedTableFields(results);
          const visibleFields = supportedFields.filter((field) => {
            return !hiddenFields.includes(`f${field.id}`);
          });
          if (visibleFields.length === 0) {
            return [];
          }

          if (columnOrder.length === 0) {
            return visibleFields;
          }

          return sortBy(visibleFields, (field) => {
            return columnOrder.indexOf(field.id) === -1
              ? Number.MAX_SAFE_INTEGER
              : columnOrder.indexOf(field.id);
          });
        },
      }
    );

  return { fields: fieldsResponse, isLoadingFields };
}

const columnHelper = createColumnHelper<APIRecord>();

export function useTableColumns({
  fields,
  blockId,
  tableId,
  canUpdateRecords,
  canDeleteRecords,
}: {
  fields: APIFieldLatest[];
  blockId: string | undefined;
  tableId: string | undefined;
  canUpdateRecords: boolean;
  canDeleteRecords: boolean;
}) {
  const { isThumbnail } = usePageContext();

  const columns = useMemo(() => {
    if (!blockId || !tableId) {
      return [];
    }

    const fieldColumns = fields.map((field) => {
      return columnHelper.accessor(
        (row) => {
          return row.data[`f${field.id}`];
        },
        {
          id: `f${field.id}`,
          header: ({ header }) => {
            return (
              <TableHeaderCell
                tableHeaderElementId={getTableHeaderElementId({
                  header,
                  tableId,
                })}
                fieldIcon={field.options?.icon}
                isFieldLocked={field.config?.locked}
                fieldName={field.name}
                sortDirection={header.column.getIsSorted()}
                canSortField={header.column.getCanSort()}
                onSort={header.column.getToggleSortingHandler()}
              />
            );
          },
          enableSorting: SUPPORTED_TABLE_BLOCK_SORT_BY_FIELD_TYPES.includes(
            field.type
          ),
          cell: ({ row, getValue }) => {
            const fieldType = field.type as SupportedTableBlockFields;

            const cellRenderer = CellRenderers[fieldType];
            if (!cellRenderer) {
              return null;
            }

            return cellRenderer({
              field,
              blockId,
              tableId,
              isDisabled: isThumbnail === true,
              record: row.original,
              value: getValue(),
              canUpdateRecords,
            });
          },
          /**
           * These values are arbitrary.
           */
          minSize: field.type === "text" ? 400 : 200,
        }
      );
    });

    if (!canDeleteRecords && !canUpdateRecords) {
      return fieldColumns;
    }

    const actionsColumn = columnHelper.display({
      id: TABLE_ROW_ACTIONS_COLUMN_ID,
      cell: ({ row }) => {
        return (
          <TableRowActionsMenu
            fields={fields}
            canDeleteRecords={canDeleteRecords}
            canEditRecords={canUpdateRecords}
            record={row.original}
            tableId={tableId}
            tableBlockId={blockId}
          />
        );
      },
      enableSorting: false,
      enableResizing: false,
    });

    return [...fieldColumns, actionsColumn];
  }, [
    fields,
    isThumbnail,
    blockId,
    tableId,
    canUpdateRecords,
    canDeleteRecords,
  ]);

  return columns;
}

function hasRemovedLastRecordOnLastPage(
  queryData:
    | {
        results: unknown[];
        meta: {
          limit?: number | undefined;
          cursor?: string | undefined;
          next_cursor?: string | undefined;
        };
        links: {
          next?: string | undefined;
          prev?: string | undefined;
        };
      }
    | undefined
) {
  if (!queryData) {
    return false;
  }

  const { results, links } = queryData;

  const isRemovingLastRecordOnPage = results.length === 1;
  const canGoToPreviousPage = links.prev != undefined;

  return isRemovingLastRecordOnPage && canGoToPreviousPage;
}

function calculateQueryRecordsLimit({
  pageSize,
  cursorQuery,
  recordsCount,
  hasMultipleBlockFilters,
}: {
  pageSize: number;
  cursorQuery: string | undefined;
  recordsCount: number | null | undefined;
  hasMultipleBlockFilters: boolean;
}) {
  if (hasMultipleBlockFilters === true) {
    /**
     * We do not support pagination in that case, so there is no need to calculate the limit.
     */
    return undefined;
  }

  if (recordsCount == null) {
    return pageSize;
  }

  if (cursorQuery === undefined) {
    return pageSize;
  }

  /**
   * When the user is fetching the last page, we need to ensure we limit the results to the remaining records.
   *
   * Since fetching the last page means paginating backwards, if we do not do this,
   * the backend will return `pageSize` records starting from the last record. This would be very confusing for the user.
   *
   * Consider the case where the table has 11 records.
   * 1. Set the pageSize to 10.
   * 2. Fetch the last page.
   *
   * Without this calculation, we would present the user with 10 records – confusing.
   * With this calculation, we will present the user with only one record – correct.
   */

  const decodedCursorQuery = cursorQuery
    ? JSON.parse(Buffer.from(cursorQuery, "base64").toString("utf-8"))
    : undefined;

  const isFetchingLastPage =
    decodedCursorQuery?.query_type === "before" &&
    !decodedCursorQuery?.before_cursor;

  if (!isFetchingLastPage) {
    return pageSize;
  }

  const remainingRecords = recordsCount % pageSize;
  if (remainingRecords === 0) {
    return pageSize;
  }

  return remainingRecords;
}

function sortOrderToSortingState(
  sortOrder: Array<RecordOrder> = []
): SortingState {
  return sortOrder
    .filter((order): order is Required<RecordOrder> => {
      return order.key != null && order.direction != null;
    })
    .map((order) => {
      return {
        id: order.key,
        desc: order.direction === "desc",
      };
    });
}

function sortingStateToSortOrder(
  sortingState: SortingState
): Array<RecordOrder> {
  return sortingState.map((order) => {
    return {
      key: order.id,
      direction: order.desc ? "desc" : "asc",
    };
  });
}
